import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faCopy, faCodeCompare, faCalendarDays } from "@fortawesome/free-solid-svg-icons";
import { buildUrl, fetchJson } from "../shared/api";
import { Markdown, normalizeMarkdown } from "../shared/markdown";
import { parseReleaseDate, flagBadge, effectiveFlag } from "../shared/releaseNotes";
import { compareVersionsAsc, compareVersionsDesc } from "../shared/version";
import { Tooltip } from "../shared/Tooltip";
import type { ConnectorInfo, IdmInfo, ModuleInfo, ReleaseNote, ReleaseNoteTag } from "../shared/types/api";

type ElementType = "idm" | "module" | "connector";

// One selectable element in the dropdown. IdM products are deduplicated by
// repository — /idm/all returns one entry per major, all sharing the repo.
type ElementOption = {
    repository: string;
    label: string;
};

// All IdM majors share one repository (CzechIdMng); in the dropdown the
// product is presented under its business name instead of the repo name
const IDM_PRODUCT_NAME = "IdStory IdM";

const ELEMENT_ENDPOINTS: Record<ElementType, string> = {
    idm: "/idm/all",
    module: "/module/all",
    connector: "/connector/all",
};

function isElementType(value: string | null): value is ElementType {
    return value === "idm" || value === "module" || value === "connector";
}

function DiffsPage() {
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    // the whole filter lives in the URL, so the current configuration can be
    // shared just by copying the link
    const params = new URLSearchParams(location.search);
    const typeParam = params.get("type");
    const elementType: ElementType = isElementType(typeParam) ? typeParam : "module";
    const repository = params.get("repository") ?? "";
    const fromVersion = params.get("from") ?? "";
    const toVersion = params.get("to") ?? "";

    const [elements, setElements] = useState<ElementOption[]>([]);
    const [availableTags, setAvailableTags] = useState<string[]>([]);

    const [notes, setNotes] = useState<ReleaseNote[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [linkCopied, setLinkCopied] = useState(false);

    const applyFilter = (type: ElementType, repo: string, from: string, to: string) => {
        const next = new URLSearchParams();
        next.set("type", type);
        if (repo) next.set("repository", repo);
        if (from) next.set("from", from);
        if (to) next.set("to", to);
        navigate({ search: `?${next.toString()}` }, { replace: true });
    };

    // load the element dropdown for the selected type
    useEffect(() => {
        let cancelled = false;
        setError(null);

        (async () => {
            try {
                const data = await fetchJson<(IdmInfo | ModuleInfo | ConnectorInfo)[]>(
                    buildUrl(ELEMENT_ENDPOINTS[elementType])
                );
                if (cancelled) return;

                const byRepo = new Map<string, ElementOption>();
                for (const item of data) {
                    if (!item.nameOfRepo || byRepo.has(item.nameOfRepo)) continue;
                    byRepo.set(item.nameOfRepo, {
                        repository: item.nameOfRepo,
                        // IdM entries carry the major in tableName (e.g. "IdM 15.x.x"),
                        // which is misleading for a whole-repository selection
                        label: elementType === "idm" ? IDM_PRODUCT_NAME : (item.tableName || item.nameOfRepo),
                    });
                }
                const options = Array.from(byRepo.values()).sort((a, b) =>
                    a.label.localeCompare(b.label, i18n.language || "cs-CZ", { sensitivity: "base" })
                );
                setElements(options);
            } catch (err: unknown) {
                if (!cancelled) setError(t("common.error"));
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [elementType, t, i18n.language]);

    // with a single element there is nothing to choose — preselect it
    useEffect(() => {
        if (elements.length === 1 && !repository) {
            applyFilter(elementType, elements[0].repository, fromVersion, toVersion);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [elements, repository]);

    // load available version tags for the selected element
    useEffect(() => {
        if (!repository) {
            setAvailableTags([]);
            return;
        }

        let cancelled = false;
        setError(null);

        (async () => {
            try {
                const tags = await fetchJson<ReleaseNoteTag[]>(
                    buildUrl("/release-note/tags", { repository })
                );
                if (cancelled) return;
                setAvailableTags(tags.map((tag) => tag.tag_name).sort(compareVersionsDesc));
            } catch (err: unknown) {
                if (!cancelled) setError(t("common.error"));
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [repository, t, i18n.language]);

    // both bounds are inclusive; the backend expects from <= to, so swap when
    // the user picked them the other way round
    const [rangeFrom, rangeTo] =
        compareVersionsAsc(fromVersion, toVersion) <= 0
            ? [fromVersion, toVersion]
            : [toVersion, fromVersion];

    // load release notes for the selected range
    useEffect(() => {
        if (!repository || !rangeFrom || !rangeTo) {
            setNotes([]);
            return;
        }

        let cancelled = false;
        setIsLoading(true);
        setError(null);

        (async () => {
            try {
                const data = await fetchJson<ReleaseNote[]>(
                    buildUrl("/release-note/range", { repository, from: rangeFrom, to: rangeTo })
                );
                if (!cancelled) setNotes(data);
            } catch (err: unknown) {
                if (!cancelled) setError(t("common.error"));
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [repository, rangeFrom, rangeTo, t, i18n.language]);

    const handleTypeChange = (type: ElementType) => {
        setElements([]);
        applyFilter(type, "", "", "");
    };

    const handleElementChange = (repo: string) => {
        applyFilter(elementType, repo, "", "");
    };

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            setLinkCopied(true);
            window.setTimeout(() => setLinkCopied(false), 2000);
        } catch (err: unknown) {
            // Clipboard API is unavailable (e.g. insecure context) — the URL
            // stays in the address bar, so the user can still copy it manually
        }
    };

    return (
        <div className="page-card">
            <h2 className="page-title">
                <FontAwesomeIcon icon={faCodeCompare} /> {t("diffs.title")}
            </h2>

            <p className="search-hint">{t("diffs.hint")}</p>

            <div style={{ display: "flex", flexWrap: "wrap", gap: "20px", marginBottom: "20px" }}>
                <div>
                    <label className="filter-label">{t("diffs.typeLabel")}</label>
                    <select className="filter-input" style={{ width: "auto", minWidth: "160px" }}
                        value={elementType}
                        onChange={(e) => handleTypeChange(e.target.value as ElementType)}
                    >
                        <option value="idm">{t("diffs.types.idm")}</option>
                        <option value="module">{t("diffs.types.module")}</option>
                        <option value="connector">{t("diffs.types.connector")}</option>
                    </select>
                </div>

                <div>
                    <label className="filter-label">{t("diffs.elementLabel")}</label>
                    <select className="filter-input" style={{ width: "auto", minWidth: "220px" }}
                        value={repository}
                        onChange={(e) => handleElementChange(e.target.value)}
                    >
                        <option value="">{t("diffs.selectPlaceholder")}</option>
                        {elements.map((el) => (
                            <option key={el.repository} value={el.repository}>
                                {el.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="filter-label">{t("diffs.fromLabel")}</label>
                    <select className="filter-input" style={{ width: "auto", minWidth: "160px" }}
                        value={fromVersion}
                        disabled={!repository}
                        onChange={(e) => applyFilter(elementType, repository, e.target.value, toVersion)}
                    >
                        <option value="">{t("diffs.selectPlaceholder")}</option>
                        {availableTags.map((tag) => (
                            <option key={tag} value={tag}>
                                {tag}
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="filter-label">{t("diffs.toLabel")}</label>
                    <select className="filter-input" style={{ width: "auto", minWidth: "160px" }}
                        value={toVersion}
                        disabled={!repository}
                        onChange={(e) => applyFilter(elementType, repository, fromVersion, e.target.value)}
                    >
                        <option value="">{t("diffs.selectPlaceholder")}</option>
                        {availableTags.map((tag) => (
                            <option key={tag} value={tag}>
                                {tag}
                            </option>
                        ))}
                    </select>
                </div>

                {repository && fromVersion && toVersion && (
                    <Tooltip label={linkCopied ? t("diffs.linkCopied") : t("diffs.copyLink")} className="diffs-copy">
                        <button className="icon-button"
                            aria-label={t("diffs.copyLink")}
                            onClick={handleCopyLink}
                        >
                            <FontAwesomeIcon icon={linkCopied ? faCheck : faCopy} />
                        </button>
                    </Tooltip>
                )}
            </div>

            {repository && availableTags.length === 0 && !error && (
                <p className="search-hint">{t("diffs.noVersions")}</p>
            )}

            {isLoading && <p>{t("common.loading")}</p>}
            {error && <p className="text-error">{error}</p>}

            {!isLoading && !error && fromVersion && toVersion && (
                <div className="content-section">
                    <h3 className="search-group-title">
                        {t("diffs.resultsHeader", { from: rangeFrom, to: rangeTo, count: notes.length })}
                    </h3>

                    {notes.length === 0 && <p className="search-hint">{t("diffs.noResults")}</p>}

                    {notes.map((rn) => (
                        <div key={rn.tag_name} className="release-wrapper">
                            <div className="release-item">
                                <span style={{ fontWeight: "bold", marginRight: "10px" }}>
                                    {rn.tag_name}
                                </span>

                                {flagBadge(effectiveFlag(rn.flag, rn.tag_name)) && (
                                    <span className="badge release-flag" style={{ marginRight: "10px" }}>
                                        {flagBadge(effectiveFlag(rn.flag, rn.tag_name))}
                                    </span>
                                )}

                                <span className="badge">
                                    <FontAwesomeIcon icon={faCalendarDays} /> {parseReleaseDate(rn.released_at)?.toLocaleDateString(i18n.language || "cs-CZ") ?? "—"}
                                </span>

                                <div className="detail-box detail-box--spaced">
                                    {rn?.notLocalized && <p className="text-error"><strong>{t("diffs.notLocalized", { language: rn.fallbackLanguage})}</strong></p> }

                                    <div className="detail-box">
                                        <Markdown>
                                            {normalizeMarkdown(rn.description || "") || t("diffs.noDescription")}
                                        </Markdown>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default DiffsPage;
